import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { profileSelector } from 'selectors'
import { verifyPhone, updatePhoneNumber } from 'actions/profile'
import { updatePhoneVerificationCode } from 'util/server/mainApi'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Page from 'rui/widgets/Page'
import Thin from 'components/layout/Thin'
import Form, { baseError } from 'components/form'
import Input from 'components/form/Input'
import LinkButton from 'components/misc/LinkButton'
import { required, not } from 'components/form/validations'
import styled from 'styled-components/macro'
import { formatPhone } from 'util/format'
import { button } from 'styles/mixins/textstyles'
import Spacer from 'components/misc/Spacer'
import { Container } from 'roadie-ui'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const Button = styled(LinkButton)`
  ${button()}
`

const SidebySide = styled.div`
  display: flex;
  justify-content: space-between;
`

const BgWhite = () => <Container.Base $backgroundColor="neutral-white" />

const PhoneVerification = ({ app, registered }) => {
  const { t } = useLocalization()
  const dispatch = useDispatch()
  const { profile } = useSelector(profileSelector)
  const [newPhone, setNewPhone] = useState(false)
  const [resent, setResent] = useState(false)

  const handleData = (data) => {
    return dispatch(verifyPhone(data.code))
      .then(() => {
        if (registered) {
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              type: 'success',
              messageKey: 'dweb_flash_profile_changes_saved',
            },
          })
        }
      })
      .catch(() => {
        return baseError(t('dweb_phone_verification_incorrect_code_try_again'), 'code')
      })
  }

  const handleNewPhone = (data) => {
    return dispatch(updatePhoneNumber(data.phone_number)).then(() => {
      setNewPhone(false)
    })
  }

  const handleWrongNumber = (event) => {
    event.preventDefault()
    setNewPhone(true)
  }

  const handleCancel = (event) => {
    event.preventDefault()
    setNewPhone(false)
  }

  const handleResend = (event) => {
    event.preventDefault()
    resend()
  }

  const resend = () => {
    return dispatch(updatePhoneVerificationCode()).then(() => {
      setResent(true)
      setTimeout(() => {
        setResent(false)
      }, 30000)
      return dispatch({
        type: 'PUSH_FLASH',
        flash: {
          message: t('dweb_phone_verification_text_message_sent_to', {
            phoneNumber: formatPhone(profile.phone_number),
          }),
          type: 'success',
        },
      })
    })
  }

  const renderVerifyForm = () => {
    return (
      <Form id="verify-form" onData={handleData}>
        <Panel>
          <PanelHeading id="heading-id">
            <LocalizedText translationKey="dweb_phone_verification_confirmation_code" />
          </PanelHeading>
          <PanelSubheading id="subheading-id">
            <LocalizedRichText
              translationKey="dweb_phone_verification_enter_code_sent_to"
              params={{ phoneNumber: formatPhone(profile.phone_number) }}
            />
          </PanelSubheading>

          <Input.FourDigitCode label={t('dweb_phone_verification_four_digit_code')} name="code" validate={required} />
          <Spacer />
          <SidebySide style={{ textAlign: 'left' }}>
            <Button id="resend-code-button" disabled={resent} onClick={handleResend}>
              <LocalizedText translationKey="dweb_phone_verification_resend_code" />
            </Button>
            <Button id="wrong-number-button" onClick={handleWrongNumber}>
              <LocalizedText translationKey="dweb_phone_verification_wrong_number" />
            </Button>
          </SidebySide>

          <Input.Submit>
            <LocalizedText translationKey="dweb_phone_verification_confirm" />
          </Input.Submit>
        </Panel>
      </Form>
    )
  }

  const renderPhoneForm = () => {
    const newNumber = not(profile.phone_number).withMessage(t('dweb_validation_must_be_different_phone_number'))

    return (
      <Form id="phone-form" onData={handleNewPhone}>
        <Panel>
          <PanelHeading id="heading-id">
            <LocalizedText translationKey="dweb_phone_verification_enter_new_number" />
          </PanelHeading>
          <PanelSubheading id="subheading-id">
            <LocalizedText translationKey="dweb_phone_verification_we_will_send_new_confirmation_code" />
          </PanelSubheading>

          <Input.Phone
            label={t('dweb_phone_verification_phone_number')}
            name="phone_number"
            validate={newNumber.required()}
          />

          <Input.Submit>
            <LocalizedText translationKey="dweb_phone_verification_resend" />
          </Input.Submit>
          <Spacer height="1.6rem" />
          <Button id="cancel-button" onClick={handleCancel}>
            <LocalizedText translationKey="dweb_common_cancel" />
          </Button>
        </Panel>
      </Form>
    )
  }

  return (
    <Page background={app === 'send' && <BgWhite />}>
      <Helmet>
        <title>{t('dweb_phone_verification_confirmation_code')}</title>
      </Helmet>
      <Thin>{newPhone ? renderPhoneForm() : renderVerifyForm()}</Thin>
    </Page>
  )
}

export default PhoneVerification
